<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <h1 class="text-2xl font-bold text-gray-900">Settings</h1>
      <p class="text-gray-600">Configure your email sending preferences and campaign settings</p>
    </div>

    <!-- Partner Information -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Partner Information</h2>
        <p class="text-sm text-gray-500 mt-1">Manage your partner account details</p>
      </div>
      
      <form @submit.prevent="savePartnerName" class="p-6 space-y-6">
        <div>
          <label class="form-label">Partner Name</label>
          <input
            v-model="partnerName"
            type="text"
            class="input-field"
            required
            placeholder="Enter partner name"
          />
          <p class="text-sm text-gray-500 mt-1">
            This name identifies your partner account across the system.
          </p>
        </div>
        
        <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            :disabled="savingPartnerName"
            class="btn-primary"
          >
            <span v-if="savingPartnerName" class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </span>
            <span v-else>Save Partner Name</span>
          </button>
        </div>
      </form>
    </div>

    <!-- Settings Form -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Email Sending Preferences</h2>
        <p class="text-sm text-gray-500 mt-1">Configure when and how your emails are sent</p>
      </div>
      
      <form @submit.prevent="saveSettings" class="p-6 space-y-6">
        <!-- Weekend Sending -->
        <div>
          <label class="flex items-center">
            <input
              v-model="settings.sendOnWeekends"
              type="checkbox"
              class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span class="ml-2 text-sm font-medium text-gray-700">Send emails on weekends</span>
          </label>
          <p class="text-sm text-gray-500 mt-1">
            When enabled, emails will be sent on Saturdays and Sundays according to your preferred time.
          </p>
        </div>

        <!-- Preferred Send Time Range -->
        <div>
          <label class="form-label">Preferred Send Time Range</label>
          <div class="flex items-center space-x-4">
            <div class="flex-1">
              <label class="text-sm text-gray-600 mb-1 block">Start Time</label>
              <div class="flex items-center space-x-2">
                <input
                  v-model="settings.preferredSendTimeStart"
                  type="time"
                  class="input-field flex-1"
                  required
                  @change="validateTimeRange"
                />
                <span class="text-sm text-gray-500 whitespace-nowrap">({{ timezoneLabel }})</span>
              </div>
            </div>
            <div class="flex-1">
              <label class="text-sm text-gray-600 mb-1 block">End Time</label>
              <div class="flex items-center space-x-2">
                <input
                  v-model="settings.preferredSendTimeEnd"
                  type="time"
                  class="input-field flex-1"
                  required
                  @change="validateTimeRange"
                />
                <span class="text-sm text-gray-500 whitespace-nowrap">({{ timezoneLabel }})</span>
              </div>
            </div>
          </div>
          <p class="text-sm text-gray-500 mt-1">
            Emails will only be sent within this time range each day. Campaign scheduled times must also fall within this range. Times are displayed in your local timezone ({{ timezoneLabel }}). Times are stored in UTC and the system automatically handles timezone conversions and midnight crossovers.
          </p>
          <p v-if="timeRangeError" class="text-sm text-red-600 mt-1">
            {{ timeRangeError }}
          </p>
        </div>

        <!-- Max Emails Per Day -->
        <div>
          <label class="form-label">Maximum Emails Per Day</label>
          <input
            v-model="settings.maxEmailsPerDay"
            type="number"
            min="100"
            max="100000"
            step="100"
            class="input-field w-48"
            required
          />
          <p class="text-sm text-gray-500 mt-1">
            The maximum number of emails that can be sent per day across all campaigns.
          </p>
        </div>

        <!-- From Name -->
        <div>
          <label class="form-label">From Name (Optional)</label>
          <input
            v-model="settings.fromName"
            type="text"
            class="input-field w-96"
            placeholder="From Name"
          />
          <p class="text-sm text-gray-500 mt-1">
            Default sender name for all campaigns. This can be overridden per campaign. If not set, only the email address will be used.
          </p>
        </div>

        <!-- From Email -->
        <div>
          <label class="form-label">From Email (Optional)</label>
          <input
            v-model="settings.fromEmail"
            type="email"
            class="input-field w-96"
            placeholder="noreply@yourdomain.com"
          />
          <p class="text-sm text-gray-500 mt-1">
            Default sender email address for all campaigns. This can be overridden per campaign. If not set, emails will be sent from noreply@[domain].
          </p>
        </div>

        <!-- Reply-To Email -->
        <div>
          <label class="form-label">Reply-To Email (Optional)</label>
          <input
            v-model="settings.replyToEmail"
            type="email"
            class="input-field w-96"
            placeholder="reply@example.com"
          />
          <p class="text-sm text-gray-500 mt-1">
            Default reply-to email address for all campaigns. This can be overridden per campaign. If not set, replies will go to the sender address.
          </p>
        </div>

        <!-- Blackout Days -->
        <div>
          <label class="form-label">Blackout Days</label>
          <div class="space-y-3">
            <div class="flex items-center space-x-3">
              <input
                v-model="newBlackoutDate"
                type="date"
                class="input-field w-48"
              />
              <button
                type="button"
                @click="addBlackoutDay"
                class="btn-secondary"
                :disabled="!newBlackoutDate"
              >
                Add Date
              </button>
            </div>
            
            <div v-if="settings.blackoutDays.length > 0" class="space-y-2">
              <p class="text-sm text-gray-500">Current blackout days:</p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="date in settings.blackoutDays"
                  :key="date"
                  class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                >
                  {{ formatDate(date) }}
                  <button
                    type="button"
                    @click="removeBlackoutDay(date)"
                    class="ml-2 text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </span>
              </div>
            </div>
            
            <p class="text-sm text-gray-500">
              Emails will not be sent on these dates, regardless of other settings.
            </p>
          </div>
        </div>

        <!-- Schedule Preview -->
        <div>
          <label class="form-label">Schedule Preview</label>
          <div class="flex items-center space-x-3">
            <input
              v-model="schedulePreview.startDate"
              type="date"
              class="input-field w-48"
            />
            <select
              v-model="schedulePreview.days"
              class="input-field w-32"
            >
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
            </select>
            <button
              type="button"
              @click="generateSchedulePreview"
              class="btn-secondary"
            >
              Generate Preview
            </button>
          </div>
          
          <div v-if="schedulePreview.data" class="mt-4">
            <div class="grid grid-cols-7 gap-2">
              <div
                v-for="day in schedulePreview.data"
                :key="day.date"
                class="p-3 border rounded-lg text-center"
                :class="day.canSend ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'"
              >
                <div class="text-xs font-medium text-gray-500">{{ day.dayOfWeek }}</div>
                <div class="text-sm font-medium">{{ formatDate(day.date) }}</div>
                <div class="text-xs mt-1">
                  <span v-if="day.canSend" class="text-green-600">
                    {{ day.preferredTime }}
                  </span>
                  <span v-else class="text-red-600">
                    {{ day.reason }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Save Button -->
        <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            @click="resetSettings"
            class="btn-secondary"
          >
            Reset to Defaults
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="btn-primary"
          >
            <span v-if="saving" class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </span>
            <span v-else>Save Settings</span>
          </button>
        </div>
      </form>
    </div>

    <!-- Additional Settings -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Advanced Settings</h2>
        <p class="text-sm text-gray-500 mt-1">Additional configuration options</p>
      </div>
      
      <div class="p-6 space-y-4">
        <!-- Email Scaling -->
        <div>
          <h3 class="text-md font-medium text-gray-900 mb-2">Email Scaling Algorithm</h3>
          <div class="bg-gray-50 p-4 rounded-lg">
            <p class="text-sm text-gray-600 mb-3">
              Your emails will be sent using an intelligent scaling algorithm:
            </p>
            <ul class="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Starts at <strong>1000 emails per day</strong></li>
              <li>Increases by <strong>20% every X days</strong> if domain score improves</li>
              <li>Maintains current rate if score drops by <strong>less than 5%</strong></li>
              <li>Reduces rate if score drops by <strong>more than 5%</strong></li>
            </ul>
          </div>
        </div>

        <!-- Domain Monitoring -->
        <div>
          <h3 class="text-md font-medium text-gray-900 mb-2">Domain Monitoring</h3>
          <div class="bg-gray-50 p-4 rounded-lg">
            <p class="text-sm text-gray-600 mb-3">
              Domain reputation scores are automatically calculated based on:
            </p>
            <ul class="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Delivery rates from Mailgun</li>
              <li>Bounce rates and reasons</li>
              <li>Complaint rates</li>
              <li>Overall sending volume and consistency</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Clubs Management -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Clubs Management</h2>
        <p class="text-sm text-gray-500 mt-1">Organize your email lists into clubs for better management</p>
      </div>
      <div class="p-6">
        <ClubsEditor />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { settingsApi, partnersApi } from '../services/api'
import { toast } from 'vue3-toastify'
import { usePartnerStore } from '../stores/partner'
import ClubsEditor from '../components/ClubsEditor.vue'
import { utcTimeToLocal, localTimeToUtc, getTimezoneName, formatTimezoneOffset } from '../utils/timezone'

// State
const settings = ref({
  sendOnWeekends: true,
  preferredSendTime: '09:00',
  preferredSendTimeStart: '09:00',
  preferredSendTimeEnd: '17:00',
  maxEmailsPerDay: 10000,
  blackoutDays: [],
  replyToEmail: '',
  fromName: '',
  fromEmail: ''
})

const timeRangeError = ref('')

const newBlackoutDate = ref('')
const saving = ref(false)
const partnerStore = usePartnerStore()
const partnerName = ref('')
const savingPartnerName = ref(false)
const schedulePreview = ref({
  startDate: '',
  days: 7,
  data: null
})

// Computed
const timezoneLabel = computed(() => {
  return getTimezoneName() || formatTimezoneOffset()
})

// Methods
const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString()
}

const fetchSettings = async () => {
  try {
    const response = await settingsApi.get()
    if (response.data.success) {
      const data = response.data.data
      // Extract time from TIME format (HH:MM:SS) to HH:MM for time inputs
      const formatTimeForInput = (timeValue) => {
        if (!timeValue) return null
        
        // Handle different formats SQL Server might return TIME as
        let timeString = ''
        if (typeof timeValue === 'string') {
          timeString = timeValue
        } else if (timeValue instanceof Date) {
          // If it's a Date object, format it
          const hours = String(timeValue.getHours()).padStart(2, '0')
          const minutes = String(timeValue.getMinutes()).padStart(2, '0')
          return `${hours}:${minutes}`
        } else {
          // Try to convert to string
          timeString = String(timeValue)
        }
        
        // Extract HH:MM from HH:MM:SS or HH:MM:SS.mmm
        // Handle formats like "09:00:00" or "09:00:00.0000000"
        const match = timeString.match(/^(\d{1,2}):(\d{2})/)
        if (match) {
          const hours = String(parseInt(match[1])).padStart(2, '0')
          const minutes = match[2]
          return `${hours}:${minutes}`
        }
        
        return null
      }
      
      console.log('Settings data received:', data)
      console.log('PreferredSendTimeStart raw:', data.PreferredSendTimeStart, typeof data.PreferredSendTimeStart)
      console.log('PreferredSendTimeEnd raw:', data.PreferredSendTimeEnd, typeof data.PreferredSendTimeEnd)
      
      // Convert UTC times from server to local time for display
      const startTimeUtc = formatTimeForInput(data.PreferredSendTimeStart)
      const endTimeUtc = formatTimeForInput(data.PreferredSendTimeEnd)
      const preferredTimeUtc = formatTimeForInput(data.PreferredSendTime)
      
      // Convert UTC to local time for display
      const startTimeLocal = startTimeUtc ? utcTimeToLocal(startTimeUtc) : null
      const endTimeLocal = endTimeUtc ? utcTimeToLocal(endTimeUtc) : null
      const preferredTimeLocal = preferredTimeUtc ? utcTimeToLocal(preferredTimeUtc) : null
      
      console.log('UTC times from server:', { startTimeUtc, endTimeUtc, preferredTimeUtc })
      console.log('Converted to local times:', { startTimeLocal, endTimeLocal, preferredTimeLocal })
      
      settings.value = {
        sendOnWeekends: data.SendOnWeekends,
        preferredSendTime: preferredTimeLocal || '09:00',
        preferredSendTimeStart: startTimeLocal || preferredTimeLocal || '09:00',
        preferredSendTimeEnd: endTimeLocal || '17:00',
        maxEmailsPerDay: data.MaxEmailsPerDay,
        blackoutDays: data.BlackoutDays ? JSON.parse(data.BlackoutDays) : [],
        replyToEmail: data.ReplyToEmail || '',
        fromName: data.FromName || '',
        fromEmail: data.FromEmail || ''
      }
      
      console.log('Settings formatted:', settings.value)
    }
  } catch (error) {
    console.error('Error fetching settings:', error)
    toast.error('Failed to fetch settings')
  }
}

const validateTimeRange = () => {
  timeRangeError.value = ''
  if (!settings.value.preferredSendTimeStart || !settings.value.preferredSendTimeEnd) {
    return true // Let required validation handle empty fields
  }
  
  const startTime = new Date(`2000-01-01 ${settings.value.preferredSendTimeStart}:00`)
  const endTime = new Date(`2000-01-01 ${settings.value.preferredSendTimeEnd}:00`)
  
  if (endTime <= startTime) {
    timeRangeError.value = 'End time must be after start time'
    return false
  }
  
  return true
}

const saveSettings = async () => {
  if (!validateTimeRange()) {
    return
  }
  
  try {
    saving.value = true
    
    // Convert local times to UTC for storage
    const preferredSendTimeStartUtc = localTimeToUtc(settings.value.preferredSendTimeStart)
    const preferredSendTimeEndUtc = localTimeToUtc(settings.value.preferredSendTimeEnd)
    const preferredSendTimeUtc = localTimeToUtc(settings.value.preferredSendTime)
    
    console.log('Local times from form:', { 
      start: settings.value.preferredSendTimeStart, 
      end: settings.value.preferredSendTimeEnd,
      preferred: settings.value.preferredSendTime
    })
    console.log('Converting to UTC for storage:', { 
      start: preferredSendTimeStartUtc, 
      end: preferredSendTimeEndUtc,
      preferred: preferredSendTimeUtc
    })
    
    const payload = {
      sendOnWeekends: settings.value.sendOnWeekends,
      preferredSendTimeStart: preferredSendTimeStartUtc,
      preferredSendTimeEnd: preferredSendTimeEndUtc,
      maxEmailsPerDay: settings.value.maxEmailsPerDay,
      blackoutDays: settings.value.blackoutDays,
      replyToEmail: settings.value.replyToEmail || null,
      fromName: settings.value.fromName || null,
      fromEmail: settings.value.fromEmail || null
    }
    
    // Only include preferredSendTime if it's a valid value (for backward compatibility)
    if (preferredSendTimeUtc) {
      payload.preferredSendTime = preferredSendTimeUtc
    }
    
    const response = await settingsApi.update(payload)
    
    if (response.data.success) {
      toast.success('Settings saved successfully')
    }
  } catch (error) {
    console.error('Error saving settings:', error)
    toast.error('Failed to save settings')
  } finally {
    saving.value = false
  }
}

const addBlackoutDay = () => {
  if (newBlackoutDate.value && !settings.value.blackoutDays.includes(newBlackoutDate.value)) {
    settings.value.blackoutDays.push(newBlackoutDate.value)
    newBlackoutDate.value = ''
  }
}

const removeBlackoutDay = (date) => {
  const index = settings.value.blackoutDays.indexOf(date)
  if (index > -1) {
    settings.value.blackoutDays.splice(index, 1)
  }
}

const resetSettings = () => {
  if (confirm('Are you sure you want to reset all settings to defaults?')) {
    settings.value = {
      sendOnWeekends: true,
      preferredSendTime: '09:00',
      preferredSendTimeStart: '09:00',
      preferredSendTimeEnd: '17:00',
      maxEmailsPerDay: 10000,
      blackoutDays: [],
      replyToEmail: '',
      fromName: '',
      fromEmail: ''
    }
    timeRangeError.value = ''
    toast.success('Settings reset to defaults')
  }
}

const generateSchedulePreview = async () => {
  if (!schedulePreview.value.startDate) {
    toast.error('Please select a start date')
    return
  }

  try {
    const response = await settingsApi.get(`/schedule-preview?startDate=${schedulePreview.value.startDate}&days=${schedulePreview.value.days}`)
    if (response.data.success) {
      schedulePreview.value.data = response.data.data.schedule
    }
  } catch (error) {
    console.error('Error generating schedule preview:', error)
    toast.error('Failed to generate schedule preview')
  }
}

const savePartnerName = async () => {
  if (!partnerName.value || partnerName.value.trim() === '') {
    toast.error('Partner name is required')
    return
  }
  
  try {
    savingPartnerName.value = true
    const response = await partnersApi.update(partnerStore.partnerId, {
      partnerName: partnerName.value.trim()
    })
    
    if (response.data.success) {
      toast.success('Partner name updated successfully')
      // Refresh partner data
      await partnerStore.fetchPartner()
    }
  } catch (error) {
    console.error('Error saving partner name:', error)
    // Error toast is handled by API interceptor
  } finally {
    savingPartnerName.value = false
  }
}

// Watch for partner changes
watch(() => partnerStore.partner, (newPartner) => {
  if (newPartner) {
    partnerName.value = newPartner.PartnerName || ''
  }
}, { immediate: true })

// Lifecycle
onMounted(async () => {
  await fetchSettings()
  
  // Load partner name
  if (partnerStore.partner) {
    partnerName.value = partnerStore.partner.PartnerName || ''
  }
  
  // Set default preview start date to today
  const today = new Date()
  schedulePreview.value.startDate = today.toISOString().split('T')[0]
})
</script> 